import React, { useRef, useState } from "react";
import { useBuilder, BlockConfig } from "../BuilderContext";
import { GetBaseURL } from "../../../utils/urlUtils";
import { ImageIcon, Loader2 } from "lucide-react";

const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/svg+xml", "image/webp"];
const MAX_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB

async function uploadImage(documentId: string, file: File): Promise<{ key: string }> {
  const formData = new FormData();
  formData.append("file", file);

  const token = localStorage.getItem("accessToken");
  const res = await fetch(`${GetBaseURL()}/api/invoices/${documentId}/images`, {
    method: "POST",
    headers: { "x-auth-token": token || "" },
    body: formData,
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || "Image upload failed");
  }

  return res.json();
}

const ImageBlock: React.FC<{ block: BlockConfig }> = ({ block }) => {
  const { state, dispatch } = useBuilder();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const imageKey = block.settings.imageKey;
  const imageWidth = block.settings.imageWidth ?? 100;
  const imageAlign = block.settings.imageAlign ?? "left";

  const alignClass =
    imageAlign === "center"
      ? "text-center"
      : imageAlign === "right"
        ? "text-right"
        : "text-left";

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset input so the same file can be re-selected
    e.target.value = "";

    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError("Unsupported file type. Please use JPG, PNG, SVG, or WebP.");
      return;
    }

    if (file.size > MAX_SIZE_BYTES) {
      setError("File exceeds 5 MB limit.");
      return;
    }

    setError(null);
    setUploading(true);

    try {
      const { key } = await uploadImage(state.documentId, file);
      dispatch({
        type: "UPDATE_BLOCK_SETTINGS",
        blockId: block.id,
        settings: { imageKey: key },
      });
    } catch (err: any) {
      setError(err.message || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  // ── Image uploaded ────────────────────────────────────────────────────
  if (imageKey) {
    const src = `${GetBaseURL()}/api/invoices/images/${encodeURIComponent(imageKey)}`;
    return (
      <div className={`py-4 ${alignClass}`}>
        <img
          src={src}
          alt=""
          className="inline-block"
          style={{ width: `${imageWidth}%` }}
        />
      </div>
    );
  }

  // ── Dropzone (no image) ───────────────────────────────────────────────
  return (
    <div className="py-4 print:hidden">
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        className="w-full border-2 border-dashed border-slate-300 rounded-xl p-8 flex flex-col items-center justify-center gap-2 transition-colors hover:border-emerald-400 hover:bg-emerald-50/30 focus:outline-none focus:border-emerald-500 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {uploading ? (
          <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
        ) : (
          <ImageIcon className="w-8 h-8 text-slate-400" />
        )}
        <span className="text-sm font-medium text-slate-600">
          {uploading ? "Uploading..." : "Click to upload image"}
        </span>
        {!uploading && (
          <span className="text-xs text-slate-400">JPG, PNG, SVG, WebP (max 5MB)</span>
        )}
      </button>

      {error && (
        <p className="text-xs text-red-500 mt-2 text-center">{error}</p>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.svg,.webp"
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
};

export default ImageBlock;
